// The heatmap's popup: opens on hover or focus and closes once the pointer has left both
// the tile and the card. Only one is ever open: opening one closes the last, so moving
// down a column never leaves two open. It overlaps the tile's far side by a fifth, level
// with the tile's middle, so the pointer reaches it without crossing a gap and the rest
// of the tile and the tiles below stay free to hover. A click pins it open, unless the
// trigger has its own click; Escape or a tap elsewhere closes it. Selecting text in it pins
// it too, so it stays while the text is commented on (``review``), and pressing the comment
// button or composer is not a tap elsewhere. Rendered into the body so the table's scroll
// box cannot clip it. The programme's bars use it too.
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { REVIEW_CHROME, REVIEW_PORTAL, useReview } from "../../utils/reviewState";
import { useClickOutside } from "../../utils/useClickOutside";
import styles from "./amazonOverview.module.css";

const MARGIN = 8;
/** How much of the tile the card covers, so the pointer can cross straight onto it. */
const OVERLAP = 0.2;
/** How long the card waits after the pointer leaves, for it to arrive on the card. */
const LINGER_MS = 150;

/** Closes whichever card is open, so opening another never leaves two up. */
let closeOpen: (() => void) | null = null;

export const HoverCard = ({
  trigger,
  children,
  className,
  label,
  style,
  onClick,
  review,
}: {
  trigger?: ReactNode;
  children: ReactNode;
  className: string;
  label: string;
  /** Where the trigger sits, for one placed absolutely (a programme bar). */
  style?: CSSProperties;
  /** The trigger's own click, in place of pinning the card. */
  onClick?: () => void;
  /** The document a selection in the card is a comment on (``data-review-doc``), where
   * the page takes comments (ReviewProvider). */
  review?: string;
}) => {
  const card = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [place, setPlace] = useState<CSSProperties>({ visibility: "hidden" });
  const linger = useRef<number | undefined>(undefined);
  const stay = () => window.clearTimeout(linger.current);
  const close = useCallback(() => {
    window.clearTimeout(linger.current);
    setOpen(false);
    setPinned(false);
  }, []);
  const anchor = useClickOutside<HTMLButtonElement>(open, close, [card], REVIEW_CHROME);
  const portalId = useReview()?.portalId;

  const show = () => {
    stay();
    if (closeOpen !== close) closeOpen?.();
    closeOpen = close;
    setOpen(true);
  };
  // A selection still being made in the card, dragged past its edge or begun by a press
  // that blurred the trigger, keeps it open.
  const selecting = () => {
    const selection = window.getSelection();
    return (
      !!selection && !selection.isCollapsed && !!card.current?.contains(selection.anchorNode)
    );
  };
  const hide = () => {
    if (pinned || selecting()) return;
    stay();
    linger.current = window.setTimeout(close, LINGER_MS);
  };

  useEffect(() => () => window.clearTimeout(linger.current), []);

  const pinOnSelection = () => {
    if (selecting()) setPinned(true);
  };

  // Over the tile's right fifth, level with its middle; over its left fifth, opening
  // leftwards, where there is no room on the right.
  const position = useCallback(() => {
    const at = anchor.current?.getBoundingClientRect();
    const box = card.current;
    if (!at || !box) return;
    const overlap = at.width * OVERLAP;
    const right = at.right - overlap;
    const left =
      right + box.offsetWidth <= window.innerWidth - MARGIN
        ? right
        : Math.max(MARGIN, at.left + overlap - box.offsetWidth);
    const middle = at.top + at.height / 2 - box.offsetHeight / 2;
    const top = Math.min(
      Math.max(MARGIN, middle),
      window.innerHeight - box.offsetHeight - MARGIN,
    );
    setPlace({ top, left });
  }, [anchor]);

  // Placed again whenever it grows, as when a list inside it opens.
  useLayoutEffect(() => {
    if (!open || !card.current) return;
    position();
    const watch = new ResizeObserver(position);
    watch.observe(card.current);
    return () => watch.disconnect();
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    addEventListener("keydown", onKey);
    addEventListener("scroll", position, true);
    return () => {
      removeEventListener("keydown", onKey);
      removeEventListener("scroll", position, true);
      if (closeOpen === close) closeOpen = null;
    };
  }, [open, position, close]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={className}
        style={style}
        aria-label={label}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        // A trigger with its own click acts and closes the card, before anything inside it
        // (an edit-mode step) handles the click and stops it here.
        onClickCapture={onClick ? close : undefined}
        onClick={() => {
          if (onClick) {
            onClick();
            return;
          }
          show();
          setPinned(true);
        }}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={card}
            role="dialog"
            aria-label={label}
            className={styles.card}
            style={place}
            onMouseEnter={stay}
            onMouseLeave={hide}
            onMouseUp={pinOnSelection}
            onKeyUp={pinOnSelection}
            {...(review && portalId ? { [REVIEW_PORTAL]: portalId, "data-review-doc": review } : {})}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
};
